import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminCustomerPasswordReset from './AdminCustomerPasswordReset';
import { exportCustomersCsv } from '../admin/adminExportUtils';

function formatJoined(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function formatAddress(addr) {
  if (!addr) return null;
  const cityLine = [addr.city, addr.state].filter(Boolean).join(', ');
  const pin = addr.pincode ? (cityLine ? `${cityLine} — ${addr.pincode}` : addr.pincode) : cityLine;
  return { lines: [addr.name, addr.phone, addr.address, pin].filter(Boolean), label: addr.label };
}

export default function AccountAdminCustomers({ listCustomers, resetCustomerPassword, embedded = false }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rolesSupported, setRolesSupported] = useState(true);
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    const res = await listCustomers();
    setLoading(false);
    if (!res.ok) {
      setError(res.error || 'Could not load customers.');
      setCustomers([]);
      return;
    }
    setCustomers(res.customers);
    setRolesSupported(res.rolesSupported !== false);
  }, [listCustomers]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => {
      const addr = c.default_address;
      const hay = [
        c.username,
        c.display_name,
        c.email,
        c.phone,
        c.last_order_number,
        addr?.name,
        addr?.phone,
        addr?.address,
        addr?.city,
        addr?.pincode,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [customers, query]);

  const body = (
    <>
      <p className={embedded ? 'customer-account__inline-lead small mb-3' : 'account-password-card__lead small text-muted mb-3'}>
        Use this list when someone calls or messages: find them by <strong>name, email, phone, or order number</strong>.
        If email reset is down, expand a customer and set a new password below. Full order lines, payment, and
        tracking are in <Link to="/admin">Catalog admin → Orders</Link> (search by order # or shipping name).
      </p>
      {!rolesSupported ? (
        <div className="alert alert-warning py-2 small mb-3" role="alert">
          Customer accounts need the <code>role</code> column on <code>admin_users</code>. Run{' '}
          <code>php scripts/apply-admin-roles-migration.php</code> then refresh.
        </div>
      ) : null}
      <div className="account-customers-toolbar mb-3">
        <input
          type="search"
          className="form-control checkout-input account-customers-search"
          placeholder="Search name, email, phone, order #, address…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search customers"
        />
        <span className="account-customers-count small text-muted">
          {loading ? 'Loading…' : `${filtered.length} customer${filtered.length === 1 ? '' : 's'}`}
        </span>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={loading || !filtered.length}
          onClick={() => exportCustomersCsv(filtered)}
          title="Opens in Excel"
        >
          Export CSV
        </button>
      </div>
      {loading ? (
        <p className="small text-muted mb-0">Loading customers…</p>
      ) : error ? (
        <div className="alert alert-warning py-2 small mb-0" role="alert">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <p className="small text-muted mb-0">
          {customers.length === 0
            ? 'No customers yet. They appear here after guest checkout or signing up on the shop.'
            : 'No customers match your search.'}
        </p>
      ) : (
        <ul className="account-customers-cards list-unstyled mb-0">
          <li className="account-customers-cards__header" aria-hidden="true">
            <span>Customer</span>
            <span>Email &amp; phone</span>
            <span>Orders</span>
            <span />
          </li>
          {filtered.map((c) => {
            const isOpen = expandedId === c.id;
            const addr = formatAddress(c.default_address);
            return (
              <li key={c.id} className={`account-customer-card${isOpen ? ' is-open' : ''}`}>
                <button
                  type="button"
                  className="account-customer-card__summary"
                  onClick={() => setExpandedId(isOpen ? null : c.id)}
                  aria-expanded={isOpen}
                >
                  <span className="account-customer-card__main">
                    <span className="account-customers-table__name">{c.display_name || c.username}</span>
                    <span className="account-customers-table__user small text-muted">@{c.username}</span>
                  </span>
                  <span className="account-customer-card__contact small">
                    {c.email ? (
                      <span className="account-customer-card__email">{c.email}</span>
                    ) : null}
                    {c.phone ? (
                      <span className="account-customer-card__phone text-muted">{c.phone}</span>
                    ) : null}
                    {!c.email && !c.phone ? <span className="text-muted">No contact on file</span> : null}
                  </span>
                  <span className="account-customer-card__orders small">
                    <strong>{c.order_count ?? 0}</strong> order{(c.order_count ?? 0) === 1 ? '' : 's'}
                    {c.last_order_number ? (
                      <span className="d-block text-muted">Last #{c.last_order_number}</span>
                    ) : null}
                  </span>
                  <span className="account-customer-card__chevron small text-muted" aria-hidden="true">
                    {isOpen ? '▲' : '▼'}
                  </span>
                </button>
                {isOpen ? (
                  <div className="account-customer-card__detail">
                    <div className="row g-3 small">
                      <div className="col-md-6">
                        <h4 className="account-customer-card__detail-title">Contact</h4>
                        <p className="mb-1">
                          <strong>{c.display_name || '—'}</strong>
                        </p>
                        {c.email ? (
                          <p className="mb-1">
                            <a href={`mailto:${c.email}`}>{c.email}</a>
                          </p>
                        ) : null}
                        {c.phone ? <p className="mb-1">{c.phone}</p> : null}
                        <p className="text-muted mb-0">Joined {formatJoined(c.created_at)}</p>
                      </div>
                      <div className="col-md-6">
                        <h4 className="account-customer-card__detail-title">Orders</h4>
                        {(c.order_count ?? 0) > 0 ? (
                          <>
                            <p className="mb-1">
                              {c.order_count} order{c.order_count === 1 ? '' : 's'}
                              {c.last_order_at ? ` · last ${formatWhen(c.last_order_at)}` : ''}
                            </p>
                            {c.last_order_number ? (
                              <p className="mb-2">
                                Latest: <strong>#{c.last_order_number}</strong>
                              </p>
                            ) : null}
                            <Link to="/admin" className="btn btn-outline-primary btn-sm rounded-pill">
                              Open orders in admin
                            </Link>
                          </>
                        ) : (
                          <p className="text-muted mb-0">No orders yet.</p>
                        )}
                      </div>
                      {addr ? (
                        <div className="col-12">
                          <h4 className="account-customer-card__detail-title">
                            Saved address ({addr.label})
                          </h4>
                          {addr.lines.map((line) => (
                            <p key={line} className="mb-0">
                              {line}
                            </p>
                          ))}
                          <p className="text-muted small mt-2 mb-0">
                            Checkout may use a different address on their latest order — check admin Orders for
                            that shipment.
                          </p>
                        </div>
                      ) : (
                        <div className="col-12">
                          <p className="text-muted small mb-0">No saved address in their account yet.</p>
                        </div>
                      )}
                      {resetCustomerPassword ? (
                        <div className="col-12">
                          <hr className="my-2" />
                          <AdminCustomerPasswordReset
                            customer={c}
                            resetCustomerPassword={resetCustomerPassword}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );

  if (embedded) return body;

  return (
    <section className="account-customers" aria-labelledby="account-customers-heading">
      <h2 id="account-customers-heading" className="account-section-label">
        Shop customers
      </h2>
      <div className="account-password-card">{body}</div>
    </section>
  );
}
